import { useEffect, useMemo, useRef, useState } from 'react';
import { buildTooltipHtml, normalizeConstituencyId } from './constituencyTooltip';
import { createConstituencyPath, navigateTo } from '../navigation';

const SVG_PATH = '/img/svg/indiaStateConstitiency2024.svg';

function IndiaConstituencyMap() {
  const [data, setData] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, html: '' });
  const [svgMarkup, setSvgMarkup] = useState('');
  const mapRef = useRef(null);

  useEffect(() => {
    fetch('/json/statesConstituency2024.json')
      .then((response) => response.json())
      .then((records) => setData(records))
      .catch(() => setData([]));

    fetch(SVG_PATH)
      .then((response) => response.text())
      .then((markup) => setSvgMarkup(markup))
      .catch(() => setSvgMarkup(''));
  }, []);

  const lookup = useMemo(() => {
    return new Map(data.map((item) => [String(item.constCode).toLowerCase(), item]));
  }, [data]);

  const aliasLookup = useMemo(() => {
    const aliases = new Map();
    data.forEach((record) => {
      [record.constCode, record.constCodeOld, record.stateCd, record.stateName, record.constName]
        .forEach((alias) => {
          const normalizedAlias = normalizeConstituencyId(alias);
          if (normalizedAlias && !aliases.has(normalizedAlias)) {
            aliases.set(normalizedAlias, record);
          }
        });
    });
    return aliases;
  }, [data]);

  useEffect(() => {
    if (!svgMarkup || !mapRef.current) {
      return;
    }

    const container = mapRef.current;
    container.innerHTML = svgMarkup;

    const svg = container.querySelector('svg');
    if (!svg) {
      return;
    }

    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.style.width = '100%';
    svg.style.height = 'auto';
    svg.style.display = 'block';

    for (const element of svg.querySelectorAll('[id]')) {
      const constCode = String(element.id || '').trim();
      const record = lookup.get(constCode.toLowerCase()) || aliasLookup.get(normalizeConstituencyId(constCode));

      if (!record) {
        continue;
      }

      element.style.cursor = 'pointer';
      element.style.transition = 'fill 0.15s ease';
      element.setAttribute('tabindex', '0');
      element.setAttribute('role', 'link');
      element.setAttribute('aria-label', `View ${record.constName} election results`);

      const baseFill = element.getAttribute('fill');
      if (!baseFill || baseFill === '#FFFFFF' || baseFill === 'white') {
        element.setAttribute('fill', '#dbeafe');
      }
      element.setAttribute('data-base-fill', element.getAttribute('fill') || '#dbeafe');
    }

    const getShapeRecord = (target) => {
      let element = target instanceof Element ? target : null;

      while (element && element !== svg) {
        if (element.id) {
          const constCode = element.id.trim();
          const record = lookup.get(constCode.toLowerCase()) || aliasLookup.get(normalizeConstituencyId(constCode));
          if (record) {
            return { element, record };
          }
        }
        element = element.parentElement;
      }

      return null;
    };

    const showTooltip = (event, element, record) => {
      const html = buildTooltipHtml(record);
      if (!html) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const shapeRect = element.getBoundingClientRect();
      const hasPointerPosition = Number.isFinite(event.clientX) && Number.isFinite(event.clientY);
      const x = hasPointerPosition
        ? event.clientX - containerRect.left + 18
        : shapeRect.left - containerRect.left + shapeRect.width / 2;
      const y = hasPointerPosition
        ? event.clientY - containerRect.top + 18
        : shapeRect.top - containerRect.top + shapeRect.height / 2;

      setTooltip({ visible: true, x, y, html });
    };

    const handlePointerOver = (event) => {
      if (event.pointerType === 'touch') {
        return;
      }

      const match = getShapeRecord(event.target);
      if (!match || (event.relatedTarget instanceof Node && match.element.contains(event.relatedTarget))) {
        return;
      }

      match.element.setAttribute('fill', '#f59e0b');
      showTooltip(event, match.element, match.record);
    };

    const handlePointerMove = (event) => {
      if (event.pointerType === 'touch') {
        return;
      }

      const match = getShapeRecord(event.target);
      if (match) {
        showTooltip(event, match.element, match.record);
      }
    };

    const handlePointerOut = (event) => {
      const match = getShapeRecord(event.target);
      if (!match || (event.relatedTarget instanceof Node && match.element.contains(event.relatedTarget))) {
        return;
      }

      match.element.setAttribute('fill', match.element.getAttribute('data-base-fill') || '#dbeafe');
      setTooltip((current) => ({ ...current, visible: false }));
    };

    const handleFocusIn = (event) => {
      const match = getShapeRecord(event.target);
      if (match) {
        showTooltip(event, match.element, match.record);
      }
    };

    const handleFocusOut = (event) => {
      const match = getShapeRecord(event.target);
      if (match && !(event.relatedTarget instanceof Node && match.element.contains(event.relatedTarget))) {
        setTooltip((current) => ({ ...current, visible: false }));
      }
    };

    const handleClick = (event) => {
      const match = getShapeRecord(event.target);
      if (match) {
        navigateTo(createConstituencyPath(match.record.constName));
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const match = getShapeRecord(event.target);
      if (match) {
        event.preventDefault();
        navigateTo(createConstituencyPath(match.record.constName));
      }
    };

    svg.addEventListener('pointerover', handlePointerOver);
    svg.addEventListener('pointermove', handlePointerMove);
    svg.addEventListener('pointerout', handlePointerOut);
    svg.addEventListener('focusin', handleFocusIn);
    svg.addEventListener('focusout', handleFocusOut);
    svg.addEventListener('click', handleClick);
    svg.addEventListener('keydown', handleKeyDown);

    return () => {
      svg.removeEventListener('pointerover', handlePointerOver);
      svg.removeEventListener('pointermove', handlePointerMove);
      svg.removeEventListener('pointerout', handlePointerOut);
      svg.removeEventListener('focusin', handleFocusIn);
      svg.removeEventListener('focusout', handleFocusOut);
      svg.removeEventListener('click', handleClick);
      svg.removeEventListener('keydown', handleKeyDown);
      setTooltip({ visible: false, x: 0, y: 0, html: '' });
    };
  }, [data, lookup, aliasLookup, svgMarkup]);

  return (
    <div className="india-map-wrapper">
      <div ref={mapRef} className="india-map" aria-label="India constituency map" />
      {tooltip.visible && (
        <div
          className="constituency-tooltip-box"
          dangerouslySetInnerHTML={{ __html: tooltip.html }}
          style={{ left: tooltip.x, top: tooltip.y }}
        />
      )}
    </div>
  );
}

export default IndiaConstituencyMap;
